import type { Localization } from "@mui/material/locale";
import { enUS as muiEnUS } from "@mui/material/locale";
import { enUS as pickerEnUS } from "@mui/x-date-pickers/locales";
import type { Locale as DateFnsLocale } from "date-fns";
import dateFnsEnUS from "date-fns/locale/en-US";
import type { TFunction } from "i18next";

export const ENABLED_APP_LOCALES = ["en", "es", "fr", "zh-CN", "zh-TW", "ko", "ja"] as const;
export const APP_LOCALES = ENABLED_APP_LOCALES;

export type EnabledAppLocale = (typeof ENABLED_APP_LOCALES)[number];
export type AppLocale = EnabledAppLocale;

type PickerLocalization = typeof pickerEnUS;

export interface AppLocaleMetadata {
    tag: AppLocale;
    nativeName: string;
    intlLocale: string;
    direction: "ltr" | "rtl";
}

export interface AppLocaleConfig extends AppLocaleMetadata {
    dateFnsLocale: DateFnsLocale;
    muiLocale: Localization;
    pickerLocale: PickerLocalization;
}

export const APP_LOCALE_CONFIGS: Record<AppLocale, AppLocaleMetadata> = {
    en: {
        tag: "en",
        nativeName: "English",
        intlLocale: "en-US",
        direction: "ltr",
    },
    es: {
        tag: "es",
        nativeName: "Español",
        intlLocale: "es-ES",
        direction: "ltr",
    },
    fr: {
        tag: "fr",
        nativeName: "Français",
        intlLocale: "fr-FR",
        direction: "ltr",
    },
    "zh-CN": {
        tag: "zh-CN",
        nativeName: "简体中文",
        intlLocale: "zh-CN",
        direction: "ltr",
    },
    "zh-TW": {
        tag: "zh-TW",
        nativeName: "繁體中文",
        intlLocale: "zh-TW",
        direction: "ltr",
    },
    ko: {
        tag: "ko",
        nativeName: "한국어",
        intlLocale: "ko-KR",
        direction: "ltr",
    },
    ja: {
        tag: "ja",
        nativeName: "日本語",
        intlLocale: "ja-JP",
        direction: "ltr",
    },
};

const dateFnsLocaleMap: Record<string, DateFnsLocale> = {
    [APP_LOCALE_CONFIGS.en.intlLocale]: dateFnsEnUS,
};

const loadedLocaleConfigs: Partial<Record<AppLocale, AppLocaleConfig>> = {
    en: {
        ...APP_LOCALE_CONFIGS.en,
        dateFnsLocale: dateFnsEnUS,
        muiLocale: muiEnUS,
        pickerLocale: pickerEnUS,
    },
};

export const registerAppLocaleConfig = (config: AppLocaleConfig): void => {
    loadedLocaleConfigs[config.tag] = config;
    dateFnsLocaleMap[config.intlLocale] = config.dateFnsLocale;
};

export const getAppLocaleConfig = (locale: AppLocale | undefined): AppLocaleConfig => {
    const resolvedLocale = locale ?? "en";
    const config = loadedLocaleConfigs[resolvedLocale];
    if (!config) {
        throw new Error(`${resolvedLocale}: locale runtime has not been loaded.`);
    }
    return config;
};

export const getEnabledLocaleConfigs = (): AppLocaleMetadata[] => {
    return ENABLED_APP_LOCALES.map((locale) => APP_LOCALE_CONFIGS[locale]);
};

export const getDateFnsLocaleMap = (): Record<string, DateFnsLocale> => {
    return dateFnsLocaleMap;
};

export const getPickerLocaleText = (locale: AppLocale | undefined, t: TFunction) => {
    const baseLocaleText = getAppLocaleConfig(locale).pickerLocale.components.MuiLocalizationProvider.defaultProps.localeText;
    const viewLabel = (view: string) => t(`muiPicker:views.${view}`);
    const catalogLocaleText: typeof baseLocaleText = {
        ...baseLocaleText,
        previousMonth: t("muiPicker:navigation.previousMonth"),
        nextMonth: t("muiPicker:navigation.nextMonth"),
        openPreviousView: t("muiPicker:navigation.openPreviousView"),
        openNextView: t("muiPicker:navigation.openNextView"),
        calendarViewSwitchingButtonAriaLabel: (view) => t(view === "year" ? "muiPicker:navigation.switchToCalendar" : "muiPicker:navigation.switchToYear"),
        start: t("muiPicker:range.start"),
        end: t("muiPicker:range.end"),
        cancelButtonLabel: t("muiPicker:actions.cancel"),
        clearButtonLabel: t("muiPicker:actions.clear"),
        okButtonLabel: t("muiPicker:actions.ok"),
        todayButtonLabel: t("muiPicker:actions.today"),
        datePickerToolbarTitle: t("muiPicker:toolbar.date"),
        dateTimePickerToolbarTitle: t("muiPicker:toolbar.dateTime"),
        timePickerToolbarTitle: t("muiPicker:toolbar.time"),
        dateRangePickerToolbarTitle: t("muiPicker:toolbar.dateRange"),
        clockLabelText: (view, time, adapter) =>
            t(time === null ? "muiPicker:clock.noSelection" : "muiPicker:clock.selected", {
                view: viewLabel(view),
                time: time === null ? "" : adapter.format(time, "fullTime"),
            }),
        hoursClockNumberText: (hours) => t("muiPicker:clock.hourNumber", { value: hours }),
        minutesClockNumberText: (minutes) => t("muiPicker:clock.minuteNumber", { value: minutes }),
        secondsClockNumberText: (seconds) => t("muiPicker:clock.secondNumber", { value: seconds }),
        selectViewText: (view) => t("muiPicker:clock.selectView", { view: viewLabel(view) }),
        calendarWeekNumberHeaderLabel: t("muiPicker:week.header"),
        calendarWeekNumberAriaLabelText: (weekNumber) => t("muiPicker:week.ariaLabel", { value: weekNumber }),
        openDatePickerDialogue: (value, utils) =>
            t(value !== null && utils.isValid(value) ? "muiPicker:dialog.dateSelected" : "muiPicker:dialog.dateEmpty", {
                value: value !== null && utils.isValid(value) ? utils.format(value, "fullDate") : "",
            }),
        openTimePickerDialogue: (value, utils) =>
            t(value !== null && utils.isValid(value) ? "muiPicker:dialog.timeSelected" : "muiPicker:dialog.timeEmpty", {
                value: value !== null && utils.isValid(value) ? utils.format(value, "fullTime") : "",
            }),
        timeTableLabel: t("muiPicker:table.time"),
        dateTableLabel: t("muiPicker:table.date"),
    };

    return catalogLocaleText;
};
